import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { toast } from "sonner";
import { Save, Loader2, Lock, Trash2, AlertTriangle } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function SettingsPanel() {
  const { active, refresh, select } = useServer();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const guildId = active?.id;

  useEffect(() => {
    if (!guildId || !active.bot_present) return;
    api.get(`/bot/settings?guild_id=${guildId}`).then((r) => setS(r.data));
  }, [guildId, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;
  if (!s) return <div className="text-zinc-500 font-mono-tabular text-sm">LOADING SETTINGS…</div>;

  const update = (k, v) => setS({ ...s, [k]: v });

  const customWelcomeLocked = active.tier === "free";

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/bot/settings?guild_id=${guildId}`, s);
      toast.success("Settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-panel" className="space-y-8 max-w-3xl">
      <div>
        <div className="overline mb-2">// CONFIGURATION</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Bot Settings</h1>
        <p className="text-zinc-400 mt-2">Configuring <span className="text-white">{active.name}</span></p>
      </div>

      <div className="tactical-card p-6 md:p-8 space-y-6">
        <Field label={`Welcome Message ${customWelcomeLocked ? "(Free tier — locked to default)" : ""}`} hint="Use {user} as placeholder.">
          <textarea className="tactical-input min-h-[80px]" value={s.welcome_message}
                    disabled={customWelcomeLocked}
                    onChange={(e) => update("welcome_message", e.target.value)}
                    data-testid="settings-welcome-message" />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Welcome Channel">
            <input className="tactical-input" value={s.welcome_channel}
                   onChange={(e) => update("welcome_channel", e.target.value)}
                   data-testid="settings-welcome-channel" />
          </Field>
          <Field label="Command Prefix">
            <input className="tactical-input" value={s.prefix}
                   onChange={(e) => update("prefix", e.target.value)}
                   data-testid="settings-prefix" />
          </Field>
          <Field label="AI Default Language">
            <select className="tactical-input" value={s.ai_default_language}
                    onChange={(e) => update("ai_default_language", e.target.value)}
                    data-testid="settings-ai-lang">
              <option value="auto">Auto-detect</option>
              <option value="en">English</option>
              <option value="ar">Arabic</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="tr">Turkish</option>
            </select>
          </Field>
          <Field label="Welcome Enabled">
            <Toggle value={s.welcome_enabled} onChange={(v) => update("welcome_enabled", v)} testId="toggle-welcome" />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
          <ToggleCard label="AI Module" value={s.ai_enabled} onChange={(v) => update("ai_enabled", v)} testId="toggle-ai-card" />
          <div className={`border p-4 flex items-center justify-between rounded-lg ${customWelcomeLocked ? "border-yellow-500/40 bg-yellow-500/5" : "border-brand-line"}`}>
            <div className="flex items-center gap-3">
              <Lock className="w-4 h-4 text-yellow-400" />
              <div>
                <div className="font-heading text-sm uppercase tracking-wider">Custom Welcome</div>
                <div className="text-[10px] text-zinc-500 font-mono-tabular uppercase">{customWelcomeLocked ? "STARTER PLAN +" : "ENABLED"}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-brand-line">
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="settings-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Settings
          </button>
        </div>
      </div>

      <div className="tactical-card p-6 md:p-8 border-red-500/40 bg-red-500/5" data-testid="danger-zone">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-red-500/10 border border-red-500/40 rounded-lg shrink-0">
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div className="flex-1">
            <div className="overline text-red-400">// DANGER ZONE</div>
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight mt-1">Remove Bot from Server</h2>
            <p className="text-sm text-zinc-400 mt-2">
              The bot will leave <span className="text-white font-semibold">{active.name}</span> and all its
              configuration data (welcome messages, levels, custom commands, giveaways, etc.) will be permanently deleted.
              You can re-add the bot anytime, but settings will start fresh.
            </p>
            <button
              onClick={async () => {
                if (!window.confirm(`Remove bot from "${active.name}"?\n\nThis will delete all bot data for this server. This action cannot be undone.`)) return;
                setRemoving(true);
                try {
                  const { data } = await api.post(`/bot/leave/${guildId}`);
                  toast.success(`Bot left ${data.guild_name}`);
                  localStorage.removeItem("606_active_guild");
                  await refresh();
                  select(null);
                  navigate("/dashboard");
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to remove bot");
                } finally {
                  setRemoving(false);
                }
              }}
              disabled={removing}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-red-500 hover:bg-red-600 text-white font-heading uppercase tracking-wider text-sm font-bold rounded-lg transition-colors disabled:opacity-50"
              data-testid="settings-remove-bot-btn"
            >
              {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Remove Bot from Server
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      {children}
      {hint && <div className="font-mono-tabular text-[10px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

function Toggle({ value, onChange, testId }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
            className={`w-full p-3 border flex items-center justify-between rounded-lg ${value ? "border-brand-orange bg-brand-orange/5" : "border-brand-line"}`}
            data-testid={testId}>
      <span className="text-sm font-semibold">{value ? "ON" : "OFF"}</span>
      <span className={`w-10 h-5 relative rounded-full ${value ? "bg-brand-orange" : "bg-brand-line"}`}>
        <span className={`absolute top-0.5 w-4 h-4 bg-black rounded-full transition-all ${value ? "left-5" : "left-0.5"}`} />
      </span>
    </button>
  );
}

function ToggleCard({ label, value, onChange, testId }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
            className={`border p-4 flex items-center justify-between rounded-lg transition-colors ${value ? "border-brand-orange bg-brand-orange/5" : "border-brand-line"}`}
            data-testid={testId}>
      <span className="font-heading text-sm uppercase tracking-wider">{label}</span>
      <span className={`w-10 h-5 relative rounded-full ${value ? "bg-brand-orange" : "bg-brand-line"}`}>
        <span className={`absolute top-0.5 w-4 h-4 bg-black rounded-full transition-all ${value ? "left-5" : "left-0.5"}`} />
      </span>
    </button>
  );
}
